/**
 * Company assets (Sonar-experience 106): vans and tools, their maintenance calendar,
 * and who each vehicle is assigned to. A DIFFERENT noun from `equipment`, which is the
 * customer's install base — these are the shop's own things.
 *
 * A leaf module like equipment.ts: takes the idempotency envelope from the spine,
 * nothing imports it back. Every input is `.strict()` — the 004 lesson (a bare
 * z.object silently strips an unknown key and returns 200; for a register writer
 * that's a saved edit that never saves). Most routers predate the lesson; this one
 * must not.
 */
import { z } from 'zod';
// Moved to a LEAF module: `spine.ts` needs it too, and this file imports FROM `spine.ts`.
// Re-exported so existing importers keep their path. See `./date`.
import { isoDate } from './date';
export { isoDate, isRealDate } from './date';
import { idempotency } from './spine';
import { listInput } from './list';

const text = (max: number) => z.string().trim().max(max).optional().or(z.literal(''));
const isoDateOpt = isoDate.optional().or(z.literal(''));

export const ASSET_KINDS = ['vehicle', 'tool'] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/**
 * Plain closed set + CHECK, not a state machine: out_of_service ↔ active flips freely
 * (a van comes back from the shop), and `retired` is terminal only by convention —
 * the archive service is the one door that sets it.
 */
export const ASSET_STATES = ['active', 'out_of_service', 'retired'] as const;
export type AssetState = (typeof ASSET_STATES)[number];

/** `custom` requires a label — "Winter tires" is a real reminder, `custom` alone is not. */
export const ASSET_MAINTENANCE_TYPES = [
  'service',
  'insurance',
  'registration',
  'inspection',
  'custom',
] as const;
export type AssetMaintenanceType = (typeof ASSET_MAINTENANCE_TYPES)[number];

/**
 * How the fleet dashboard's two cards split ONE table (the approved 110 prototype
 * shows "Maintenance due" and "Reminders due" side by side): work on the van vs
 * paperwork about the van. `custom` lands with maintenance — a hand-labelled entry
 * describes work by default ("Winter tires"), and 107 may revisit. Encoded here so
 * the two tiles cannot be invented ad hoc per page (review 106, Spec minor).
 */
export const MAINTENANCE_CARD_TYPES = ['service', 'custom'] as const;
export const REMINDER_CARD_TYPES = ['insurance', 'registration', 'inspection'] as const;
/**
 * Which card a due row belongs on. Named here rather than written out as a literal union
 * wherever a row type is declared — the web had it in three places and the whole reason
 * the partition lives in this file is that it must not be restated per surface.
 */
export type MaintenanceCard = 'maintenance' | 'reminder';

const maintenanceLabelRequired = (
  v: { type: AssetMaintenanceType; label?: string },
  ctx: z.RefinementCtx,
) => {
  if (v.type === 'custom' && !v.label?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['label'],
      message: 'A custom reminder needs a label',
    });
  }
};

export const assetCreateInput = idempotency
  .extend({
    kind: z.enum(ASSET_KINDS),
    name: z.string().trim().min(1).max(120),
    // vehicle half
    plate: text(20),
    make: text(80),
    model: text(80),
    capacityNote: text(200),
    homeBaseAddress: text(300),
    /**
     * The bridge to the parts world: a vehicle asset MAY name the stock_locations van
     * that carries its parts (kind='vehicle' rows predate this table). Optional and
     * unique — two assets must not claim one van.
     */
    stockLocationId: z.string().uuid().optional(),
    // tool half
    serialNo: text(80),
    category: text(80),
  })
  .strict();
export type AssetCreateInput = z.infer<typeof assetCreateInput>;

export const assetUpdateInput = idempotency
  .extend({
    assetId: z.string().uuid(),
    name: z.string().trim().min(1).max(120).optional(),
    status: z.enum(ASSET_STATES).optional(),
    plate: text(20),
    make: text(80),
    model: text(80),
    capacityNote: text(200),
    homeBaseAddress: text(300),
    stockLocationId: z.string().uuid().nullable().optional(),
    serialNo: text(80),
    category: text(80),
  })
  .strict();
export type AssetUpdateInput = z.infer<typeof assetUpdateInput>;

export const assetIdInput = idempotency.extend({ assetId: z.string().uuid() }).strict();
export type AssetIdInput = z.infer<typeof assetIdInput>;

/**
 * The register is a SERVER table (107): search, per-column filters, sort and paging all
 * happen in SQL, and the footer's total is a COUNT over the same predicate. Before this
 * the input was `{kind, includeRetired}` and the service returned every row it could
 * find — the `limit(500)` shape `listInput` exists to end, one entity at a time.
 *
 * The two asset-specific knobs stay: they are not columns a person filters, they are
 * which register you are looking at.
 */
export const assetListInput = listInput
  .extend({
    kind: z.enum(ASSET_KINDS).optional(),
    /** Retired rows are history: hidden unless asked for, never deleted. */
    includeRetired: z.boolean().optional(),
  })
  .strict();
export type AssetListInput = z.infer<typeof assetListInput>;

export const assetMaintenanceUpsertInput = idempotency
  .extend({
    /** Present = edit that row; absent = a new entry. */
    maintenanceId: z.string().uuid().optional(),
    assetId: z.string().uuid(),
    type: z.enum(ASSET_MAINTENANCE_TYPES),
    label: text(120),
    dueOn: isoDate,
    notes: text(2000),
  })
  .strict()
  .superRefine(maintenanceLabelRequired);
export type AssetMaintenanceUpsertInput = z.infer<typeof assetMaintenanceUpsertInput>;

export const assetMaintenanceIdInput = idempotency
  .extend({ maintenanceId: z.string().uuid() })
  .strict();
export type AssetMaintenanceIdInput = z.infer<typeof assetMaintenanceIdInput>;

export const assetAssignmentSetInput = idempotency
  .extend({
    assetId: z.string().uuid(),
    userId: z.string().uuid(),
    startsOn: isoDate,
    /** Absent = a standing assignment (the normal case: this is Marcus's van). */
    endsOn: isoDateOpt,
  })
  .strict();
export type AssetAssignmentSetInput = z.infer<typeof assetAssignmentSetInput>;

export const assetAssignmentEndInput = idempotency
  .extend({
    assignmentId: z.string().uuid(),
    endsOn: isoDate,
  })
  .strict();
export type AssetAssignmentEndInput = z.infer<typeof assetAssignmentEndInput>;

/**
 * Put a tool ON a van, effective-dated (114 part 6).
 *
 * ⚠️ Deliberately a SEPARATE verb from `assetAssignmentSetInput`, not a nullable `userId`
 * on it. The two answer different questions — *who is responsible* versus *what is on this
 * van* — and a shop really does hand a tech a van full of somebody else's tools. Folding
 * them into one input would force every caller and every reader to disambiguate a union.
 */
export const assetPlacementSetInput = idempotency
  .extend({
    /** The TOOL. `kind = 'tool'` is checked in the service — zod cannot see the row. */
    assetId: z.string().uuid(),
    /** The VAN. `kind = 'vehicle'`, same reason. */
    vehicleAssetId: z.string().uuid(),
    startsOn: isoDate,
    /** Absent = it lives on that van until somebody says otherwise (the normal case). */
    endsOn: isoDateOpt,
  })
  .strict();
export type AssetPlacementSetInput = z.infer<typeof assetPlacementSetInput>;

export const assetPlacementEndInput = idempotency
  .extend({
    placementId: z.string().uuid(),
    endsOn: isoDate,
  })
  .strict();
export type AssetPlacementEndInput = z.infer<typeof assetPlacementEndInput>;
